// @ts-expect-error moduleResolution:nodenext issue 54523
import { html, PropertyValues } from 'lit';
// @ts-expect-error moduleResolution:nodenext issue 54523
import { customElement, property } from 'lit/decorators.js';
import { loadStripe, Stripe, StripePaymentMethodMessagingElement } from '@stripe/stripe-js';
import LightElement from './light-element.js';
import SiteFonts from '../../typescript/modules/site-fonts.js';
import Logger from '../../typescript/modules/logger.js';

type StripePaymentMethodMessagingType = 'klarna' | 'afterpay_clearpay' | 'affirm';

@customElement('stripe-payment-method-messaging')
export default class StripePaymentMethodMessaging extends LightElement {

	@property({ type: Number })
	amount: number;

	@property({ type: String })
	paymentMethodType: StripePaymentMethodMessagingType;

	private static stripePromise: Promise<Stripe | null> | null = null;

	private element: StripePaymentMethodMessagingElement | null = null;

	private mountGeneration = 0;

	private stripeConfig: {
		stripePublishableApiKey: string;
		stripeFontUrl: string;
	} = {
			stripePublishableApiKey: '',
			stripeFontUrl: ''
		};

	connectedCallback() {
		super.connectedCallback();
		this.loadStripeConfig();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.unmountElement();
	}

	updated(changedProperties: PropertyValues) {
		if (changedProperties.has('amount') || changedProperties.has('paymentMethodType')) {
			this.mountElement();
		}
	}

	private loadStripeConfig() {
		const configElement = document.querySelector('.js-stripe-config') as HTMLScriptElement;
		if (configElement) {
			try {
				this.stripeConfig = JSON.parse(configElement.textContent || '{}');
			}
			catch (error) {
				Logger.trackException(new Error('Stripe Payment Method Messaging: Failed to JSON parse Stripe configuration.'));
			}
		}
	}

	private unmountElement() {
		if (this.element) {
			this.element.unmount();
			this.element = null;
		}
	}

	private async mountElement() {

		this.unmountElement();
		const generation = ++this.mountGeneration;

		if (!this.stripeConfig.stripePublishableApiKey) {
			Logger.trackException(new Error('Stripe Payment Method Messaging: Missing Stripe Publishable API Key in configuration.'));
			return;
		}

		if (this.amount == null || Number.isNaN(this.amount) || !this.paymentMethodType) {
			return;
		}

		try {
			if (!StripePaymentMethodMessaging.stripePromise) {
				StripePaymentMethodMessaging.stripePromise = loadStripe(this.stripeConfig.stripePublishableApiKey);
			}

			const stripe = await StripePaymentMethodMessaging.stripePromise;
			if (!stripe || generation !== this.mountGeneration) {
				return;
			}

			const font = SiteFonts.getFont();

			const elements = stripe.elements({
				appearance: {
					variables: {
						fontSizeBase: '15px',
						fontSizeSm: '15px',
						fontFamily: `${font?.family}, system-ui, sans-serif`,
						fontWeightMedium: 'normal',
						fontWeightNormal: 'normal',
						colorText: '#2c2e2f'
					},
					rules: {
						'.PaymentMethodMessaging': {
							textAlign: 'left'
						}
					}
				},
				fonts: font ? [
					{
						family: font.family,
						src: `url(${this.stripeConfig.stripeFontUrl}/${font.fileName}) format('woff2')`,
						weight: font.weight
					}
				] : []
			} as any);
			const element = elements.create('paymentMethodMessaging', {
				amount: Math.round(this.amount * 100),
				currency: 'GBP',
				countryCode: 'GB',
				paymentMethodTypes: [this.paymentMethodType]
			});

			const container = this.querySelector('.js-stripe-payment-method-messaging-container');
			if (container && generation === this.mountGeneration) {
				element.mount(container as HTMLElement);
				this.element = element;
			}
		}
		catch (error) {
			Logger.trackException(error instanceof Error ? error : new Error(String(error)));
		}
	}

	render() {
		return html`<div class="js-stripe-payment-method-messaging-container" data-test="stripe-payment-method-messaging-${this.paymentMethodType}"></div>`;
	}

}
